import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Flame, Keyboard, MessageCircleQuestion, Sparkles } from "lucide-react";
import { useWorkspace } from "@/stores/workspace";
import { cn } from "@/lib/cn";

/**
 * FSRS-lite scheduling — visible intervals build student trust in the system.
 * Real FSRS uses stability/difficulty/retrievability; this UI mirrors the four
 * rating buttons and shows next-review intervals so students learn to trust it.
 */
type Rating = "again" | "hard" | "good" | "easy";

const INTERVALS: Record<Rating, { label: string; color: string }> = {
  again: { label: "<1 min",  color: "bg-destructive/10 text-destructive border-destructive/30" },
  hard:  { label: "6 min",   color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  good:  { label: "2 days",  color: "bg-success/10 text-success border-success/30" },
  easy:  { label: "10 days", color: "bg-primary/10 text-primary border-primary/30" },
};

interface Card {
  id: string;
  topic: string;
  front: string;
  definition: string;
  quickTest: string;
  example: string;
}

const DECK: Card[] = [
  {
    id: "c-trap",
    topic: "Data Sufficiency",
    front: "The C-trap",
    definition: "When statements 1 and 2 look obviously sufficient together but each is independently sufficient (or insufficient) — and the test is whether you noticed.",
    quickTest: "If you instinctively picked C in under 10 seconds, re-check 1 and 2 separately.",
    example: "If x² = 16, (1) tells you x > 0, so x = 4. Sufficient alone — answer is A, not C.",
  },
  {
    id: "ps-divisibility",
    topic: "Number Properties",
    front: "Prime factorization rule",
    definition: "A positive integer's prime factorization is unique — the count of each prime fully determines its divisors.",
    quickTest: "Number of divisors of n = product of (exponent + 1) for each prime factor.",
    example: "60 = 2² · 3 · 5 → divisors: (2+1)(1+1)(1+1) = 12 divisors.",
  },
  {
    id: "cr-strengthen",
    topic: "Critical Reasoning",
    front: "Strengthen — controlled comparison",
    definition: "The strongest strengthener is usually a comparison that isolates the claimed cause by holding other variables constant.",
    quickTest: "Look for an answer that rules out an alternative explanation, not one that restates the conclusion.",
    example: "Argument: 'Glossy packaging caused 12% sales lift.' Strengthener: 'Matte-package control stores saw 0% change.'",
  },
];

const KEY_HINTS = ["1 = Again", "2 = Hard", "3 = Good", "4 = Easy", "Space = Show"];

export function Flashcards() {
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [history, setHistory] = useState<{ id: string; rating: Rating }[]>([]);

  const card = DECK[idx % DECK.length];
  const done = history.length;
  const accuracy = useMemo(() => {
    if (history.length === 0) return null;
    const ok = history.filter((h) => h.rating !== "again").length;
    return Math.round((ok / history.length) * 100);
  }, [history]);

  const rate = (rating: Rating) => {
    setHistory((h) => [...h, { id: card.id, rating }]);
    setRevealed(false);
    setIdx((i) => i + 1);
  };

  // Keyboard shortcuts (1/2/3/4 + space)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === " ") { e.preventDefault(); setRevealed(true); return; }
      if (!revealed) return;
      if (e.key === "1") rate("again");
      if (e.key === "2") rate("hard");
      if (e.key === "3") rate("good");
      if (e.key === "4") rate("easy");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="container max-w-2xl space-y-8 py-10">
      {/* Top bar */}
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => useWorkspace.getState().setView("learn")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" /> Learn
        </button>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground md:flex">
            <Flame className="size-3 text-accent" /> 11-day streak
          </div>
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">{done}</span> / 12 today
          </div>
        </div>
      </header>

      {/* Card */}
      <article className="relative rounded-3xl border border-border bg-card p-8 shadow-xl shadow-foreground/5 md:p-10">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{card.topic}</span>
          <span className="font-mono text-muted-foreground">#{idx + 1}</span>
        </div>

        <h2 className="mt-6 font-serif text-4xl font-normal leading-tight md:text-5xl">
          {card.front}
        </h2>

        {!revealed ? (
          <div className="mt-12 grid place-items-center">
            <button
              onClick={() => setRevealed(true)}
              className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:bg-foreground/90"
            >
              Show answer · <span className="font-mono opacity-70">space</span>
            </button>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            <Block label="Definition" body={card.definition} />
            <Block label="Quick test" body={card.quickTest} />
            <Block label="Example" body={card.example} />

            <button
              className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary"
              onClick={() => alert("Maya would generate a fresh example here using your existing tutor pipeline.")}
            >
              <MessageCircleQuestion className="size-3.5" />
              Ask Maya for another example
            </button>
          </div>
        )}
      </article>

      {/* Rating buttons */}
      {revealed && (
        <div className="grid grid-cols-4 gap-2">
          {(Object.keys(INTERVALS) as Rating[]).map((r) => (
            <button
              key={r}
              onClick={() => rate(r)}
              className={cn(
                "group flex flex-col items-center gap-1 rounded-2xl border bg-card px-3 py-4 transition-all hover:-translate-y-0.5",
                INTERVALS[r].color,
              )}
            >
              <span className="text-xs uppercase tracking-wider">{r}</span>
              <span className="font-mono text-xs opacity-80">{INTERVALS[r].label}</span>
              <span className="mt-1 font-mono text-[10px] opacity-50">
                {r === "again" ? "1" : r === "hard" ? "2" : r === "good" ? "3" : "4"}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Stats + shortcuts hint */}
      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          {accuracy !== null && (
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="size-3 text-primary" />
              <span className="text-foreground">{accuracy}%</span> recall today
            </span>
          )}
        </div>
        <div className="hidden items-center gap-3 md:flex">
          <Keyboard className="size-3" />
          <div className="flex gap-3 font-mono">
            {KEY_HINTS.map((h) => <span key={h}>{h}</span>)}
          </div>
        </div>
      </footer>
    </div>
  );
}

function Block({ label, body }: { label: string; body: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <p className="text-base leading-relaxed">{body}</p>
    </div>
  );
}
